import { useEffect, useRef, useState } from 'react'
import { useMobile } from 'src/hooks/useMobile'
import { createPortal } from 'react-dom'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'
import type {
  Video,
  Image as TImage,
} from 'src/components/restructure/product/sections/GalleryImage/types/gallery-image'
import { CloseButtonIcon } from 'src/components/Icons/CloseButtonIcon'
import Carousel from 'src/components/restructure/common/Carousel/Carousel'
import Thumbs from 'src/components/restructure/common/Carousel/Thumbs'
import { ImageSliderIcon } from 'src/components/Icons/ImageSliderIcon'
import { Image } from 'src/components/ui/Image'

import { useImageZoom } from './utils/index'
import './index.scss'
import { GalleryMovie } from '../GalleryMovie'

type GalleryFullViewProps = {
  isOpen: boolean
  closeGalleryView: () => void
  images: Array<TImage | Video>
  selectedImageForClick: number
}

export const GalleryFullView = ({
  isOpen,
  closeGalleryView,
  images,
  selectedImageForClick,
}: GalleryFullViewProps) => {
  const { isMobile } = useMobile()
  const mainContainerRef = useRef<HTMLDivElement>(null)
  const carouselRef = useRef<{
    showSlide: (index: number) => void
    currentIndex: number
  } | null>(null)

  const [currentIndex, setCurrentIndex] = useState(selectedImageForClick)

  const totalItemCount = images.length
  const initialSlideIndex = selectedImageForClick

  const {
    zoomedImage,
    wrapperRefs,
    pointerRef,
    rafRef,
    transformRefs,
    handleZoom,
    updateTransform,
  } = useImageZoom()

  const ZOOM_SIZE = 2000

  const clampIndex = (idx: number) =>
    idx < 0 ? images.length - 1 : idx >= images.length ? 0 : idx

  const goToSlide = (imageIndex: number) => {
    const clampedIndex = clampIndex(imageIndex)

    setCurrentIndex(clampedIndex)
    carouselRef.current?.showSlide(clampedIndex)
    transformRefs.current[clampedIndex]?.resetTransform?.()
  }

  const isCurrentZoomed =
    zoomedImage?.index === currentIndex && zoomedImage.isZoomed

  useEffect(() => {
    if (!isCurrentZoomed) {
      carouselRef.current?.showSlide(currentIndex)
    }
  }, [isCurrentZoomed, currentIndex])

  useEffect(() => {
    carouselRef.current?.showSlide(initialSlideIndex)
  }, [initialSlideIndex])

  useEffect(() => {
    setCurrentIndex(selectedImageForClick)
  }, [selectedImageForClick])

  useEffect(() => {
    if (zoomedImage?.isZoomed) {
      updateTransform(zoomedImage.index)
    }
  }, [zoomedImage, updateTransform])

  useEffect(() => {
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [zoomedImage?.isZoomed])

  if (!isOpen) {
    return null
  }

  const current = images[currentIndex]
  const isImage = (item: Video | TImage): item is TImage =>
    'imageUrl' in (item ?? {})

  return createPortal(
    <div
      data-testid="gallery-full-view"
      className="gallery-full-view fixed inset-0 flex items-center justify-center z-[999999] bg-restructure-background-primary h-100"
      ref={mainContainerRef}
    >
      <div className="w-full flex flex-col items-center m-auto h-full relative justify-between">
        <div className="section-image w-full flex flex-col justify-center items-center h-full pt-[80px]">
          <button
            className=" absolute top-4 right-4 p-2 z-10 restructure-tablet:top-6 restructure-tablet:right-6 restructure-tablet:p-4"
            data-testid="close-gallery-full-view1"
            aria-label="Botão fechar da Galeria Full View"
            onClick={closeGalleryView}
          >
            <CloseButtonIcon />
          </button>
          <div className="flex w-full h-full justify-center self-center custom-container ">
            <div
              ref={(el) => (wrapperRefs.current[currentIndex] = el)}
              className={`overflow-hidden flex self-center w-full max-w-[648px] h-full relative ${
                zoomedImage?.index === currentIndex && zoomedImage.isZoomed
                  ? 'zoomed'
                  : ''
              }`}
              role="button"
              style={{ cursor: 'zoom-in' }}
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()

                const transform = transformRefs.current[currentIndex]

                if (!transform) {
                  return
                }

                pointerRef.current = { x: e.clientX, y: e.clientY }
                handleZoom(currentIndex, transform)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleZoom(currentIndex, transformRefs.current[currentIndex])
                }
              }}
              onMouseMove={(e) => {
                if (!isCurrentZoomed) {
                  return
                }

                pointerRef.current = { x: e.clientX, y: e.clientY }
                if (rafRef.current === null) {
                  rafRef.current = requestAnimationFrame(() => {
                    rafRef.current = null
                    updateTransform(currentIndex)
                  })
                }
              }}
            >
              <TransformWrapper
                maxScale={4}
                minScale={1}
                initialScale={1}
                limitToBounds
                wheel={{ disabled: true }}
                doubleClick={{ disabled: true }}
                panning={{
                  disabled: false,
                  velocityDisabled: true,
                }}
                alignmentAnimation={{ disabled: true }}
                zoomAnimation={{ disabled: true }}
                centerOnInit={false}
                centerZoomedOut={false}
                onInit={(ref) => {
                  images.forEach((_, index) => {
                    transformRefs.current[index] = ref
                  })
                }}
              >
                <TransformComponent
                  wrapperStyle={{
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <div
                    className={
                      isCurrentZoomed && !isMobile ? 'hidden' : 'block'
                    }
                  >
                    <Carousel
                      ref={carouselRef}
                      items={images}
                      enableDrag={isMobile}
                      arrows={false}
                      initialSlide={initialSlideIndex}
                      onSlideChange={() => {
                        const slideId = clampIndex(
                          carouselRef.current?.currentIndex ?? 0
                        )

                        setCurrentIndex(slideId)
                        carouselRef.current?.showSlide(slideId)
                        transformRefs.current[slideId]?.resetTransform?.()
                      }}
                      clickCallback={(idx) => {
                        const item = images[idx]

                        if (!('url' in item)) {
                          goToSlide(idx)
                        }
                      }}
                      width={648}
                      height={648}
                    />
                  </div>

                  {isCurrentZoomed && !isMobile && (
                    <div
                      className="relative overflow-hidden"
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'zoom-out' }}
                      onClick={(e) => {
                        pointerRef.current = { x: e.clientX, y: e.clientY }
                        handleZoom(
                          currentIndex,
                          transformRefs.current[currentIndex]
                        )
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleZoom(
                            currentIndex,
                            transformRefs.current[currentIndex]
                          )
                        }
                      }}
                    >
                      {isImage(current) ? (
                        <Image
                          id={`image-${currentIndex}`}
                          className="h-full w-full object-cover !cursor-zoom-out mix-blend-multiply transition-all duration-200"
                          src={current.imageUrl}
                          width={ZOOM_SIZE}
                          height={ZOOM_SIZE}
                          alt={current.imageText ?? 'Imagem do produto'}
                          loading="eager"
                          preload
                          data-testid="image-gallery"
                          aria-label="Imagem da galeria"
                        />
                      ) : (
                        <GalleryMovie
                          url={current.url}
                          videoType={current.videoType}
                          label={current.label}
                        />
                      )}
                    </div>
                  )}
                </TransformComponent>
              </TransformWrapper>
            </div>
          </div>

          {isMobile && (
            <div
              className="absolute bottom-[10%] left-1/2 transform -translate-x-1/2"
              aria-label={`Contador de imagem: ${
                currentIndex + 1
              } de ${totalItemCount}`}
            >
              <div className="flex flex-row justify-center items-center gap-[6px] px-[6px] py-[12px]">
                <ImageSliderIcon />
                <span>
                  {currentIndex + 1}/{totalItemCount}
                </span>
              </div>
            </div>
          )}
        </div>

        {!isMobile && (
          <Thumbs
            items={images}
            onClick={goToSlide}
            currentIndex={currentIndex}
          />
        )}
      </div>
    </div>,
    document.body
  )
}
